import { Component } from 'react';
import moment from 'moment';
import { pick, isFunction, toNumber, toInteger, toString } from 'lodash';
import 'overview/containers/CreateBinder/external.scss';
import DateTimePicker from 'common/components/DateTime/DateTimePicker';
import DateOnlyPicker from 'common/components/DateOnly/DateOnlyPicker';
import { verifyInputFormat } from 'common/util/dateTime';

const ALLOWED_EVENT_LIST = [
    'onCopy', 'onCut', 'onPaste', 'onKeyDown', 'onKeyUp', 'onKeyPress',
    'onFocus', 'onBlur', 'onClick', 'onContextMenu', 'onDoubleClick',
    'onDrag', 'onDragEnd', 'onDragEnter', 'onDragExit', 'onDragLeave',
    'onDragOver', 'onDragStart', 'onDrop', 'onMouseDown', 'onMouseEnter',
    'onMouseLeave', 'onMouseMove', 'onMouseOut', 'onMouseOver', 'onMouseUp',
    'onScroll', 'onWheel'
];

export class DataTypeEditor extends Component {
    focus() {
        if (this.element && isFunction(this.element.focus)) {
            this.element.focus();
        }
    }

    select() {
        if (this.element && isFunction(this.element.select)) {
            this.element.select();
        }
    }

    render() {
        const {
            dataType,
            changeHandler,
            className,
            style,
            placeholder,
            name,
            autoComplete,
            autoFocus,
            disabled
        } = this.props;

        const formProps = {
            style,
            placeholder: placeholder,
            name,
            autoComplete,
            autoFocus,
            disabled
        };

        const handleDateChange = (date, changeHandler) => {
            const formattedDate = date ? date.toISOString() : null;
            changeHandler({ target: { value: formattedDate } });
        };

        const events = pick(this.props, ALLOWED_EVENT_LIST);
        let { value } = this.props;
        let tryValue;
        switch (dataType && dataType.toLowerCase()) {
            case 'small_text':
                return <input ref={e => this.element = e}
                    type="text"
                    value={value || ''}
                    className={className}
                    onChange={changeHandler}
                    {...formProps}
                    {...events}
                    data-cy="DataTypeEditor-text"/>;
            case 'large_text':
                return <textarea ref={e => this.element = e}
                    type="text"
                    value={value || ''}
                    className={className}
                    onChange={changeHandler}
                    {...formProps}
                    rows="10"
                    {...events} 
                    data-cy="DataTypeEditor-textarea"/>;
            case 'dateonly':
                value = moment(value, verifyInputFormat(value));
                value = value.isValid() ? moment(value) : null;
                return <DateOnlyPicker ref={e => this.element = e}
                    value={value}
                    className={className}
                    onChange={ date => handleDateChange(date, changeHandler) }
                    time={false}
                    {...formProps}
                    {...events} 
                    data-cy="DataTypeEditor-DateOnlyPicker"/>;
            case 'datetime':
                value = moment(value, verifyInputFormat(value));
                value = value.isValid() ? value : null;
                return <DateTimePicker ref={e => this.element = e}
                    value={value}
                    className={className}
                    onChange={ date => handleDateChange(date, changeHandler) }
                    time={true}
                    {...formProps}
                    {...events} 
                    data-cy="DataTypeEditor-DateTimePicker"/>;
            case 'integer':
                tryValue = parseInt(value, 10);
                if (window.isNaN(tryValue)) {
                    value = '';
                } else {
                    value = tryValue;
                }
                return <input ref={e => this.element = e}
                    step="1"
                    pattern="-?\d+"
                    type="number"
                    value={toString(value)}
                    className={className}
                    onChange={e => changeHandler({ target: { value: e.target.value ? toInteger(e.target.value) : '' } })}
                    {...formProps}
                    {...events} 
                    data-cy="DataTypeEditor-number"/>;
            case 'real':
                tryValue = parseFloat(value);
                if (window.isNaN(tryValue)) {
                    value = '';
                } else {
                    value = tryValue;
                }
                return <input ref={e => this.element = e}
                    type="number"
                    value={toString(value)}
                    className={className}
                    onChange={e => changeHandler({ target: { value: e.target.value ? toNumber(e.target.value) : '' } })}
                    {...formProps}
                    {...events} 
                    data-cy="DataTypeEditor-numbers"/>;
            case 'boolean':
                return (
                    <select ref={e => this.element = e}
                        value={!!value}
                        className={className}
                        onChange={e => changeHandler({ target: { value: e.target.value === 'true' } })}
                        {...formProps}
                        {...events}
                        data-cy="DataTypeEditor-select">
                        <option value={true} data-cy="DataTypeEditor-Yes">Yes</option>
                        <option value={false} data-cy="DataTypeEditor-No">No</option>
                    </select>
                );
            default:
                return <span tabIndex="0" {...events} data-cy="DataTypeEditor-span">{value}</span>;
        }
    }
}

export default DataTypeEditor;
